import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function extractYouTubeId(url: string) {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
}

function isImageUrl(url: string) {
  return /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(url);
}

function getLinkBehavior(href: string) {
  if (typeof window === 'undefined') {
    return { internalPath: null, external: false };
  }

  try {
    const resolvedUrl = new URL(href, window.location.origin);
    const isSameOrigin = resolvedUrl.origin === window.location.origin;

    return {
      internalPath: isSameOrigin ? `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}` : null,
      external: !isSameOrigin,
    };
  } catch {
    return { internalPath: href.startsWith('/') ? href : null, external: false };
  }
}

interface SupportSmartLinkProps {
  href: string;
  className: string;
  children: ReactNode;
}

function SupportSmartLink({ href, className, children }: SupportSmartLinkProps) {
  const navigate = useNavigate();
  const { internalPath, external } = getLinkBehavior(href);

  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
      className={className}
      onClick={(event) => {
        if (!external && internalPath) {
          event.preventDefault();
          navigate(internalPath);
        }
      }}
    >
      {children}
    </a>
  );
}

function renderImagePreview(url: string, className: string, onLoad?: () => void) {
  return (
    <SupportSmartLink
      href={url}
      className={`my-2 block overflow-hidden rounded-xl border border-border/70 bg-background/50 ${className}`}
    >
      <img src={url} alt="Shared image" className="max-h-80 w-full object-cover" onLoad={onLoad} />
    </SupportSmartLink>
  );
}

interface YouTubeEmbedCardProps {
  url: string;
  isCurrentUserMessage: boolean;
}

function YouTubeEmbedCard({ url, isCurrentUserMessage }: YouTubeEmbedCardProps) {
  const videoId = extractYouTubeId(url);
  const [title, setTitle] = useState('Watch on YouTube');
  const linkClassName = isCurrentUserMessage ? 'text-white/95 hover:text-white' : 'text-foreground hover:text-emerald-600 dark:hover:text-emerald-400';

  useEffect(() => {
    const controller = new AbortController();

    const loadTitle = async () => {
      try {
        const response = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          return;
        }

        const data = (await response.json()) as { title?: string };
        if (typeof data.title === 'string' && data.title.trim()) {
          setTitle(data.title.trim());
        }
      } catch {
        // Ignore title lookup failures and keep the fallback label.
      }
    };

    void loadTitle();

    return () => {
      controller.abort();
    };
  }, [url]);

  if (!videoId) {
    return null;
  }

  return (
    <div className="my-2 overflow-hidden rounded-xl border border-border/70 bg-background/50" style={{ width: 'min(100%, 26rem)' }}>
      <div className="border-b border-border/70 px-3 py-2">
        <SupportSmartLink href={url} className={`block text-sm font-medium underline underline-offset-4 ${linkClassName}`}>
          {title}
        </SupportSmartLink>
      </div>
      <div className="relative w-full pb-[56.25%]">
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube.com/embed/${videoId}`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

interface SupportMessageBodyProps {
  body: string;
  isCurrentUserMessage: boolean;
  onMediaLoad?: () => void;
}

export function SupportMessageBody({ body, isCurrentUserMessage, onMediaLoad }: SupportMessageBodyProps) {

  const linkClassName = isCurrentUserMessage ? 'underline text-white/90' : 'underline text-emerald-600 dark:text-emerald-400';
  const lines = body.split('\n');

  return (
    <div className="space-y-2">
      {lines.map((line, index) => {
        const urls = line.match(URL_REGEX) || [];
        const youtubeUrls = urls.filter((url) => Boolean(extractYouTubeId(url)));
        const imageUrls = urls.filter((url) => isImageUrl(url));
        const mediaUrls = [...new Set([...youtubeUrls, ...imageUrls])];
        const linkUrls = urls.filter((url) => !mediaUrls.includes(url));
        const textOnlyLine = mediaUrls.reduce((current, url) => current.replace(url, ''), line).trim();

        if (!urls.length) {
          return (
            <ReactMarkdown
              key={index}
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="whitespace-pre-wrap break-words text-sm leading-6">{children}</p>,
                a: ({ href, children }) =>
                  href ? (
                    <SupportSmartLink href={href} className={linkClassName}>
                      {children}
                    </SupportSmartLink>
                  ) : null,
                img: ({ src }) => (src ? renderImagePreview(src, '', onMediaLoad) : null),

              }}
            >
              {line}
            </ReactMarkdown>
          );
        }

        return (
          <div key={index} className="space-y-2">
            {textOnlyLine ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <p className="whitespace-pre-wrap break-words text-sm leading-6">{children}</p>,
                  a: ({ href, children }) =>
                    href ? (
                      <SupportSmartLink href={href} className={linkClassName}>
                        {children}
                      </SupportSmartLink>
                    ) : null,
                }}
              >
                {textOnlyLine}
              </ReactMarkdown>
            ) : null}

            {youtubeUrls.map((url) => (
              <YouTubeEmbedCard key={url} url={url} isCurrentUserMessage={isCurrentUserMessage} />
            ))}

            {imageUrls.map((url) => (
              <div key={url}>{renderImagePreview(url, '', onMediaLoad)}</div>
            ))}

            {linkUrls.map((url) => (
              <SupportSmartLink key={url} href={url} className={`block break-all text-sm underline ${linkClassName}`}>
                {url}
              </SupportSmartLink>
            ))}
          </div>
        );
      })}
    </div>
  );
}
